/**
 * StructureRenderModule — renders deployed SWG structures as real 3D models (G1).
 *
 * Resolves each `structure` row's model through the prefab catalog
 * (prefab_model["structure:"+type] → render_asset), loads the GLB via AssetModule,
 * seats it on the terrain (footprint-min terrainSeat at the structure's x,z) at the SWG yaw +
 * catalog scale, and builds a Rapier static collider from the asset's proxy. Because
 * it resolves through the catalog LIVE, re-pointing one prefab_model row + a re-sync
 * swaps the model across every structure of that type.
 *
 * Two render paths (G2), chosen per asset:
 *   • hasLods — a THREE.LOD (the RenderSub culling pass swaps levels by camera
 *     distance). Per-instance LOD swapping needs its own Object3D, so these stay
 *     individual meshes: added to `root` and registered with RenderCullingModule,
 *     unchanged from before.
 *   • no LOD chain — every such placement used to render its OWN `.clone()` of the
 *     shared GLB (one UBO bind per structure per frame — the WebGPURenderer CPU
 *     bottleneck, three.js#30560; the last clone-per-row render path in the client).
 *     This path now batches into ONE shared `StaticInstancer`: every renderable part
 *     of every such structure gets its own instance handle, composed onto the row's
 *     world transform (`collectMeshParts` + a structureId → handles map) — the same
 *     pattern `DecorationModule` uses for structure_item rows. Instanced batches are
 *     `frustumCulled=false` by design (StaticInstancer/InstancedMeshPool), so this
 *     path is no longer registered with RenderCullingModule — the per-clone culling
 *     registration went away along with the per-clone Object3D.
 *
 * Collider registration is unchanged and independent of the render path — every
 * placed structure, LOD or instanced, gets (and on removal loses) its own Rapier
 * static collider keyed by structureId.
 */
import type { App, IModule } from 'enginex';
import * as THREE from 'three/webgpu';
import type { DbConnection } from '../module_bindings/index.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { registerBuildingStamps, unregisterBuildingStamps, roomFitMargin, roomFitEnabled, restampReach, meshHeightUnfit, type BuildingStamp } from '../lib/terrain_stamp.js';
import { ASSET_STORE_BASE } from '../config.js';
import { groundY } from '../lib/grounding.js';
import { terrainSeat } from '../lib/terrain_seat.js';
import { setStructureSeat, clearStructureSeat, deedPlacement, type DeedPlacement } from '../lib/structure_seat.js';
import { modelYawOf } from '@shared/types/structures.js';
import { StaticInstancer, collectMeshParts } from '../lib/instancing.js';
import type { AssetModule } from './AssetModule.js';
import type { PhysicsModule } from './PhysicsModule.js';
import { interiorEligibleDeed, enterables, type EnterableBuilding } from '../lib/building_interiors.js';
import { deedEnterablePose } from '../lib/interior_capability.js';
import type { RenderCullingModule } from './RenderCullingModule.js';
import { buildLOD } from '../lib/lod.js';
import { enableShadows } from '../lib/shadows.js';
import type { ColliderProxy } from '../lib/collider_proxy.js';
import { addStructureSolid, clearStructureSolids } from '../lib/structure_solids.js';
import { registerDamageReceiver, forgetDamageReceiver } from '../lib/damage_runtime.js'; // shader gaps R2: a deed is a damage receiver
import { deedReceiverId } from '../lib/damage_state.js';

/** Solid-registry site ids for player deeds (one per structure), far below the settlement / HLOD / guild ranges. */
const DEED_SOLID_BASE = -2_000_000_000;
const deedSolidSite = (key: string): number => DEED_SOLID_BASE - Number(key);
/** A deed's box as a vehicle / ship / bolt solid (2026-09-25): deeds never registered in structure_solids, and the
 *  hovercraft's only structure check (hullBlockedAt) reads that registry and the settlement masks — so hovercraft and
 *  ships passed straight through player houses. The box is the ONE placement's (`deedPlacement`) rendered bbox, in
 *  three's yaw (the registry's convention); `masked: false` — no settlement mask knows a deed. */
function registerDeedSolid(key: string, dp: DeedPlacement): void {
  clearStructureSolids(deedSolidSite(key));
  addStructureSolid(deedSolidSite(key), {
    x: dp.boxT.x, z: dp.boxT.z, hx: dp.boxHalf[0], hz: dp.boxHalf[2], yaw: dp.boxT.yaw,
    base: dp.boxT.y - dp.boxHalf[1], top: dp.boxT.y + dp.boxHalf[1], masked: false,
  });
  registerDamageReceiver(deedReceiverId(key), dp.boxT.x, dp.boxT.y, dp.boxT.z, dp.boxT.yaw, dp.boxHalf[0], dp.boxHalf[1], dp.boxHalf[2], 'coated_metal', deedSolidSite(key)); // shader gaps R2
}

/** THE EDIT-MODE SCALE OF A DEED TYPE — the catalog scale's mirror, not a render override.
 *  History: the cooked deed GLBs normalize to ~1.9 m, and until 2026-09-23 this table multiplied them at render time.
 *  Since then a registered model is sized by `prefab_model.default_scale` ALONE (`sync()` below; a type with no model
 *  is skipped — there is no box fallback left), and the table's one reader is Edit Mode's palette
 *  (`AtlasEditModeModule` → `fitScaleFor`): a `structure:<deed type>` placed there arrives at this scale, "like the
 *  deployed structure". It had drifted from the catalog (a small house 4 against 5.18, a city hall 15 against 24.1,
 *  and the five legacy types at their pre-09-23 multipliers while the catalog drew them at 1). 2026-10-01: every
 *  value below IS the live catalog scale (out-player-city/apply-scales.sh — change the two together). */
export const TYPE_SCALE: Record<string, number> = {
  merchant_tent: 2.2146,    // 3.8 m — its own stall model (asset from out-civic-four/ingest-field.sh)
  small_house: 5.1769,      // 6 m
  medium_house: 6.338,      // 7.2 m
  large_house: 7.767,       // 9.6 m
  guild_hall: 50,           // ×2.5 (2026-08-29): the hall is the town's grandest civic monument
  factory: 9.5785,          // 10 m
  harvester: 3.8725,        // 6.5 m — its own field-machine model; equipmentOnly, so no size opens it
  cantina: 8.0972,          // 8 m
  city_hall: 24.0964,       // 32 m
  extractor_small: 3.0235,  // 5.4 m
  extractor_medium: 4.476,  // 8.5 m
  extractor_large: 5.711,   // 10 m (2026-10-01, the owner's answer: was 12.3)
};

interface PlacedLOD { kind: 'lod'; instance: THREE.LOD; assetId: bigint; release: () => void; }
/** Path (b) — non-LOD structures batch into the shared StaticInstancer: one handle per
 *  renderable part of the master GLB, plus the world transform for the `instances` hook
 *  (there's no per-row Object3D left to read position/rotation back from). */
interface PlacedInstanced { kind: 'instanced'; handles: number[]; assetId: bigint; x: number; y: number; z: number; yaw: number; release: () => void; }
type Placed = PlacedLOD | PlacedInstanced;

export class StructureRenderModule implements IModule {
  readonly name = 'StructureRender';
  private scene!: THREE.Scene;
  private readonly root = new THREE.Group();                      // path (a): LOD structures only
  private instancer: StaticInstancer | null = null;
  /** Deed terrain stamps registered per placement (unregistered on remove). */
  private readonly stampAt = new Map<string, { x: number; z: number }>();                // path (b): shared non-LOD structures
  private readonly placed = new Map<string, Placed>();             // structureId → placed model
  private readonly proxyCache = new Map<string, ColliderProxy>();  // assetId → fetched collider proxy
  private subscribed = false;
  private syncing = false;
  private syncDirty = false;

  constructor(private readonly assets: AssetModule, private readonly physics: PhysicsModule, private readonly culling: RenderCullingModule) {}

  init(app: App): void {
    this.scene = app.world.scene;
    this.scene.add(this.root);
    this.instancer = new StaticInstancer(this.scene, 4096, 'structures');
    this.instancer.setRainLayer(true); // player structures are roofs (render wave 1)
    bus.on('atlas:account-ready', () => { this.subscribe(); void this.sync(); });
    for (const ev of ['atlas:structures-updated', 'atlas:world-entered'] as const) bus.on(ev, () => void this.sync());
    (window as unknown as { __structureExterior?: unknown }).__structureExterior = (id: string) => this.exteriorShown(id); // probe lever
  }

  private subscribe(): void {
    if (this.subscribed) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.subscribed = true;
    conn.subscriptionBuilder()
      .onApplied(() => void this.sync())
      .subscribe([
        'SELECT * FROM structure', 'SELECT * FROM prefab_model', 'SELECT * FROM asset_collider',
        'SELECT * FROM render_asset', 'SELECT * FROM world', 'SELECT * FROM terrain_region',
        'SELECT * FROM structure_meta', // the console's names + entry policies (a small table)
        'SELECT * FROM structure_furnish', // the bought furnish packs
      ]);
    // 150 ms trailing debounce (doctrine law 4): each event triggers a full
    // O(structures) sync pass — a row drip caused back-to-back full scans.
    let pingTimer: number | null = null;
    const ping = (): void => {
      if (pingTimer !== null) return;
      pingTimer = window.setTimeout(() => {
        pingTimer = null;
        bus.emit('atlas:structures-updated', {});
      }, 150);
    };
    for (const tbl of [conn.db.structure, conn.db.prefabModel]) {
      const t = tbl as unknown as { onInsert(cb: () => void): void; onUpdate(cb: () => void): void; onDelete(cb: () => void): void };
      t.onInsert(ping); t.onUpdate(ping); t.onDelete(ping);
    }
  }

  private async sync(): Promise<void> {
    const conn = atlas.conn;
    // A sync request landing MID-SYNC (this loop awaits GLB decodes) must
    // RE-RUN when the current pass ends — dropping it left the previous
    // world's structures AND their colliders resident after a world hop
    // (stale commons/space colliders inside user worlds, 2026-07-30).
    if (this.syncing) { this.syncDirty = true; return; }
    if (!conn || !this.instancer) return;
    this.syncing = true;
    const instancer = this.instancer;
    try {
      const live = new Set<string>();
      // Index the prefab catalog by key ONCE per sync (O(prefabs)) so the per-structure
      // lookup below is O(1) — sync() was O(structures × prefabs): a full prefabModel scan
      // for EACH structure. sync() is event-driven (not per-frame), so one pass is cheap.
      const prefabByKey = new Map([...conn.db.prefabModel.iter()].map((p) => [p.prefabKey, p] as const));
      // Footprint-aware seat (high-relief audit 2026-08-05): the single-point
      // groundY floated a deed house's whole downhill footprint on slopes —
      // seat on the MIN across the scaled footprint like the settlement path.
      let commonsSeed: string | null = null;
      for (const w of conn.db.world.iter()) if (w.kind === 'commons') { commonsSeed = w.terrainSeed; break; }
      const seatAt = (x: number, z: number, footR: number): number =>
        commonsSeed ? terrainSeat(commonsSeed, x, z, Math.max(1, footR), 0.25, meshHeightUnfit).y : groundY(conn, x, z); // seated on the ground as it stood before any room fit (C2)
      const bbScratch = new THREE.Box3();
      let anyNewSeat = false;
      for (const s of conn.db.structure.iter()) {
        if (s.worldId !== atlas.currentWorldId) continue; // render ONLY the world the player is in — else Commons structures bleed into every personal world and never clear on a world hop
        const key = s.structureId.toString();
        const map = prefabByKey.get(`structure:${s.structureType}`);
        if (!map || map.assetId === 0n) continue;
        live.add(key);
        const existing = this.placed.get(key);
        if (existing && existing.assetId === map.assetId) continue; // already correct (transform is static)
        if (existing) this.remove(key); // model re-pointed → reload

        const row = conn.db.renderAsset.renderAssetId.find(map.assetId);
        if (row?.hasLods) {
          // Path (a): a cooked LOD chain → THREE.LOD, culled by RenderCullingModule.
          const h = await buildLOD(this.assets, map.assetId);
          if (this.placed.has(key)) { h.release(); continue; } // concurrent-sync guard
          const inst = h.object;
          bbScratch.setFromObject(inst); // model-space (position/scale applied below)
          // A REGISTERED model is sized by its catalog defaultScale alone (the SWG set, 2026-09-23: TARGET_H / mesh height set at
          // ingest); TYPE_SCALE only ever sized the box fallback and would multiply a real model many times over.
          const scaleA = (map.defaultScale || 1);
          const footA = Math.max(bbScratch.max.x - bbScratch.min.x, bbScratch.max.z - bbScratch.min.z) / 2 * scaleA;
          const y = seatAt(s.posX, s.posZ, footA * 0.7);
          // ONE placement for the render AND the collider (2026-09-23, lib/structure_seat.ts):
          // the model origin goes at dp.{x,y,z} with the seating offset INSIDE the placement
          // (null for today's well-pivoted deed GLBs — nothing moves), the proxy body takes the
          // same frame, the box fallback sits on the rendered bbox centre and the keep-out seat
          // on the seated footprint. Before, the collider and the seat assumed the mesh was
          // centred on posX/posZ with its base at y — true only by the luck of the cook.
          // THE MODEL YAW (2026-09-23): the generated set's door is on the model's +Z face; turned by π here so
          // it lands on the structure frame's −Z — where the interior door, the door approach and the map's
          // marker are. The frame (`s.yaw`, decorations, the interior) never carries it.
          const dp = deedPlacement(`structure:${s.structureType}`, s.posX, y, s.posZ, s.yaw + modelYawOf(s.structureType), scaleA, bbScratch);
          inst.position.set(dp.x, dp.y, dp.z);
          inst.rotation.y = dp.yaw;
          inst.scale.setScalar(dp.scale);
          // THE SWITCH DISTANCES ARE WORLD METRES (2026-10-01, the LOD2 fix): buildLOD measures the radius on the
          // UNSCALED model (a normalised ~1.9-unit mesh) and three's LOD.update compares them with the world camera
          // distance — so a ×24 City Hall switched to LOD1 at 5.7 m and to LOD2 at 19 m on high, standing at its door.
          // Hidden while every player-city LOD2 was secretly LOD0; a real 15 % LOD2 needs the distance the radius ×
          // the placement scale actually earns (≈ 140 m to LOD1 and ≈ 460 m to LOD2 for that City Hall on high).
          for (const lvl of inst.levels) lvl.distance *= dp.scale;
          // Seat the LOD's levels inside the placement (each level is buildLOD's own clone).
          if (dp.off) for (const lvl of inst.levels) lvl.object.position.add(new THREE.Vector3(dp.off.x, dp.off.y, dp.off.z));
          enableShadows(inst, map.castShadow, true); // G3: structures cast (per the catalog flag) + receive CSM shadows
          this.root.add(inst);
          this.culling.registerLOD(inst);
          this.placed.set(key, { kind: 'lod', instance: inst, assetId: map.assetId, release: h.release });
          // Interior foundation: a player's house/hall is enterable (industry
          // and small deeds are not). LOD-path structures hide by visibility;
          // the collider restores from the cached proxy or the box fallback.
          if (interiorEligibleDeed(s.structureType, dp.w, dp.h, dp.d)) {
            const physics = this.physics;
            const proxyCache = this.proxyCache;
            const aid = map.assetId.toString();
            this.registerDeedInterior(key, s, dp,
              () => { inst.userData.ownerHidden = true; inst.visible = false; physics.removeStaticCollider(`structure:${key}`); }, // the culler honours ownerHidden
              () => {
                inst.userData.ownerHidden = false;
                inst.visible = true;
                const proxy = proxyCache.get(aid);
                if (proxy) physics.addStaticCollider(`structure:${key}`, proxy, dp.proxyT);
                else physics.addStaticCollider(`structure:${key}`, { shape: 'box', halfExtents: dp.boxHalf }, dp.boxT);
              });
          }
          setStructureSeat(key, { y, x: dp.footX, z: dp.footZ, r: footA }); anyNewSeat = true; // decor Y + versona keep-out
          registerDeedSolid(key, dp); // hovercraft / ships / bolts meet the house (2026-09-25)
          void this.addCollider(conn, key, map.assetId, dp);
        } else {
          // Path (b): no LOD chain → the shared StaticInstancer (G2 draw-call collapse).
          // Every renderable part of the master GLB gets its own instance handle,
          // composed onto this structure's world transform — the DecorationModule
          // pattern (collectMeshParts + a row → handles map).
          // Tier-matched load: potato renders the cooked LOD1 (512² textures)
          // — the canonical LOD0 (16×2048² KTX2) was the profiled walk freeze.
          const { object: master, release } = await this.assets.loadForTier(map.assetId);
          if (this.placed.has(key)) { release(); continue; } // concurrent-sync guard
          const scale = (map.defaultScale || 1); // see scaleA above — TYPE_SCALE is the fallback's
          bbScratch.setFromObject(master);
          const footB = Math.max(bbScratch.max.x - bbScratch.min.x, bbScratch.max.z - bbScratch.min.z) / 2 * scale;
          const y = seatAt(s.posX, s.posZ, footB * 0.7);
          // The same ONE placement as path (a): render = T·R(yaw)·S(scale)·T(off), collider = the same frame.
          const dp = deedPlacement(`structure:${s.structureType}`, s.posX, y, s.posZ, s.yaw + modelYawOf(s.structureType), scale, bbScratch); // the model yaw, as path (a)
          const placeMat = new THREE.Matrix4().compose(
            new THREE.Vector3(dp.x, dp.y, dp.z),
            new THREE.Quaternion().setFromEuler(new THREE.Euler(0, dp.yaw, 0)),
            new THREE.Vector3(dp.scale, dp.scale, dp.scale),
          );
          if (dp.off) placeMat.multiply(new THREE.Matrix4().makeTranslation(dp.off.x, dp.off.y, dp.off.z));
          const handles: number[] = [];
          const parts: Array<{ geometry: THREE.BufferGeometry; material: THREE.Material; matrix: THREE.Matrix4 }> = []; // kept for the interior restore
          for (const part of collectMeshParts(master)) {
            const matrix = new THREE.Matrix4().multiplyMatrices(placeMat, part.local);
            parts.push({ geometry: part.geometry, material: part.material, matrix });
            const id = instancer.add(part.geometry, part.material, matrix);
            if (id >= 0) {
              handles.push(id);
              // StaticInstancer doesn't hand back the InstancedMesh it just created/reused
              // for this (geometry, material) batch, so the catalog's per-prefab castShadow
              // flag (G3) can't be applied the way enableShadows(clone, ...) used to — locate
              // the batch (InstancedMeshPool.meshFor) and flip the SAME
              // flags there instead (via the pool — the batch hangs under the anchor group).
              this.applyBatchShadow(part.geometry, part.material, map.castShadow, true);
            }
          }
          this.placed.set(key, { kind: 'instanced', handles, assetId: map.assetId, x: dp.x, y: dp.y, z: dp.z, yaw: dp.yaw, release });
          // Interior foundation, path (b) (2026-09-22 — it was sealed by
          // omission): the batched deed hides by dropping its instance handles
          // and restores by re-adding the master's parts (the settlement
          // producer's pattern; `handles` is the placed record's own array, so
          // teardown removes whatever is live). Collider as path (a).
          if (interiorEligibleDeed(s.structureType, dp.w, dp.h, dp.d)) {
            const physics = this.physics;
            const proxyCache = this.proxyCache;
            const aid = map.assetId.toString();
            const cast = map.castShadow;
            this.registerDeedInterior(key, s, dp,
              () => { for (const g of handles) instancer.remove(g); handles.length = 0; physics.removeStaticCollider(`structure:${key}`); },
              () => {
                if (handles.length > 0 || !this.placed.has(key)) return; // already visible / torn down meanwhile
                for (const p of parts) {
                  const g = instancer.add(p.geometry, p.material, p.matrix);
                  if (g >= 0) { handles.push(g); this.applyBatchShadow(p.geometry, p.material, cast, true); }
                }
                const proxy = proxyCache.get(aid);
                if (proxy) physics.addStaticCollider(`structure:${key}`, proxy, dp.proxyT);
                else physics.addStaticCollider(`structure:${key}`, { shape: 'box', halfExtents: dp.boxHalf }, dp.boxT);
              });
          }
          setStructureSeat(key, { y, x: dp.footX, z: dp.footZ, r: footB }); anyNewSeat = true; // decor Y + versona keep-out
          registerDeedSolid(key, dp); // hovercraft / ships / bolts meet the house (2026-09-25)
          void this.addCollider(conn, key, map.assetId, dp);
        }
      }
      for (const key of [...this.placed.keys()]) if (!live.has(key)) this.remove(key);
      // New seats registered → ping decor to re-compose on the exact parent Y.
      // Terminates: the re-entrant structure pass places nothing new (all rows
      // already match) so it never re-emits.
      if (anyNewSeat) bus.emit('atlas:structures-updated', {});
    } finally {
      this.syncing = false;
      if (this.syncDirty) { this.syncDirty = false; void this.sync(); }
    }
  }

  /** Seat a deed's registry entry + its terrain stamp (both render paths —
   *  THE INSTANCE-SPLIT LAW). The producer path owns the hide/restore closures. */
  private registerDeedInterior(
    key: string, s: { structureType: string; yaw: number }, dp: DeedPlacement,
    hide: () => void, restore: () => void,
  ): void {
    const entId = `deed:${key}`;
    // The interior's door is the frame's local −Z. The guild hall's carved entrance faces the model's +Z, like the
    // generated SWG set's, so since 2026-09-24 it turns its MODEL (modelYawOf, SET_MODEL_TYPES) instead of flipping
    // this frame — the door the placement rules test is then the one you walk through.
    const entYaw = s.yaw;
    const ent: EnterableBuilding = {
      id: entId,
      name: s.structureType.replace(/_/g, ' '),
      key: `deed:${s.structureType}`,
      // The SEATED footprint centre (where the mesh stands, not the row's raw point), the frame's own yaw and the
      // rendered size — ONE pure function, so verify:interior-coverage's yaw fixture tests the pose registered here.
      ...deedEnterablePose(entYaw, dp),
      hide, restore,
    };
    enterables.set(entId, ent);
    // terrain flattens under the placed deed structure too
    // THE ROOM FIT (interiors programme C2, 2026-10-01): a deed's stamp also CUTS the ground above its floor out to one
    // near-ring lattice quad — the stamp was finer than the terrain mesh, and the hill came through the back wall of
    // every house on a slope (a brown wedge by day, a black band at night, and the terrain trimesh under a body).
    // Cut-only; the lever `atlas:interior-ground-fit = 'off'` registers today's plain stamp.
    const st: BuildingStamp = { x: ent.x, z: ent.z, hw: ent.w / 2 + 0.8, hd: ent.d / 2 + 0.8, yaw: ent.yaw, h: ent.groundY + 0.02 };
    if (roomFitEnabled()) st.fit = roomFitMargin(ent.yaw);
    const fresh = registerBuildingStamps([st]);
    this.stampAt.set(key, { x: st.x, z: st.z });
    if (fresh.length > 0) bus.emit('atlas:terrain-restamp', { stamps: fresh.map(restampReach) }); // only a NEW stamp re-cuts the ground; the chunks its cut touches rebuild too
  }

  /** Force cast/receive shadow flags on the InstancedMesh batch a (geometry, material)
   *  pair maps to (path b only — path a keeps calling `enableShadows` directly). */
  private applyBatchShadow(geometry: THREE.BufferGeometry, material: THREE.Material, cast: boolean, receive: boolean): void {
    // The batch lives under the instancer's ANCHOR group since the 2026-08-23
    // precision fix — a scene.children scan no longer finds it; ask the pool.
    const m = this.instancer?.meshFor(geometry, material);
    if (m) enableShadows(m as THREE.Object3D, cast, receive);
  }

  /** The collider stands where the mesh stands: the cooked mesh-space proxy at the
   *  placement's own frame (`dp.proxyT`), else the box on the rendered bbox
   *  (`dp.boxT`) — both from the ONE `deedPlacement` record the render composed. */
  private async addCollider(conn: DbConnection, key: string, assetId: bigint, dp: DeedPlacement): Promise<void> {
    const col = conn.db.assetCollider.assetId.find(assetId);
    if (!col) {
      // No cooked proxy → AABB box fallback (high-relief audit 2026-08-05):
      // a proxy-less deed house was a walk-through ghost. Half-extents are
      // PRE-SCALED (scaleProxy does not scale a box → boxT carries scale 1).
      if (this.placed.has(key)) this.physics.addStaticCollider(`structure:${key}`, { shape: 'box', halfExtents: dp.boxHalf }, dp.boxT);
      return;
    }
    let proxy = this.proxyCache.get(assetId.toString());
    if (!proxy) {
      try { proxy = (await (await fetch(`${ASSET_STORE_BASE}/${col.colliderUri}`)).json()) as ColliderProxy; this.proxyCache.set(assetId.toString(), proxy); }
      catch (e) {
        // A proxy that failed to fetch is not a walk-through ghost: the box stands in.
        console.warn(`[StructureRender] collider proxy for asset ${assetId} failed — box fallback`, e);
        if (this.placed.has(key)) this.physics.addStaticCollider(`structure:${key}`, { shape: 'box', halfExtents: dp.boxHalf }, dp.boxT);
        return;
      }
    }
    if (this.placed.has(key)) this.physics.addStaticCollider(`structure:${key}`, proxy, dp.proxyT);
  }

  private remove(key: string): void {
    const p = this.placed.get(key);
    if (!p) return;
    if (p.kind === 'lod') {
      this.root.remove(p.instance);
      this.culling.unregisterLOD(p.instance);
      p.release();
    } else {
      for (const id of p.handles) this.instancer?.remove(id);
      p.release(); // release AFTER instances are gone (matched to the tier-picked cache key)
    }
    const st = this.stampAt.get(key);
    if (st) { unregisterBuildingStamps(st.x, st.z); this.stampAt.delete(key); }
    this.physics.removeStaticCollider(`structure:${key}`); // collider teardown is independent of the render representation — always runs
    enterables.delete(`deed:${key}`);
    clearStructureSeat(key);
    clearStructureSolids(deedSolidSite(key)); // the deed's vehicle solid goes with it (2026-09-25)
    forgetDamageReceiver(deedReceiverId(key)); // shader gaps R2: its records stop drawing (kept by structure id, bounded by the byte cap)
    this.placed.delete(key);
  }

  /** Test/inspection hook: how many structures are rendered. */
  get count(): number { return this.placed.size; }

  /** Probe lever: is a placed structure's exterior currently drawn? (path (a): the LOD's visibility; path (b): live handles.) */
  exteriorShown(key: string): boolean | null {
    const p = this.placed.get(key);
    if (!p) return null;
    return p.kind === 'lod' ? p.instance.visible : p.handles.length > 0;
  }

  /** Test/inspection hook: each placed instance's world transform + bound asset. */
  get instances(): { x: number; y: number; z: number; yaw: number; assetId: string }[] {
    return [...this.placed.values()].map((p) => p.kind === 'lod'
      ? { x: p.instance.position.x, y: p.instance.position.y, z: p.instance.position.z, yaw: p.instance.rotation.y, assetId: p.assetId.toString() }
      : { x: p.x, y: p.y, z: p.z, yaw: p.yaw, assetId: p.assetId.toString() });
  }
}
